import React, { useState } from 'react';
import { X, Upload, Camera, Check, Loader } from 'lucide-react';

const API   = process.env.REACT_APP_BACKEND_URL;
const BRAND = 'linear-gradient(135deg,#2cbdc4,#094f6f)';

const DOC_TYPES_AU = [
  { id: 'drivers_licence', label: 'Australian Driver\'s Licence', back: true },
  { id: 'passport',        label: 'Passport',                    back: false },
  { id: 'medicare',        label: 'Medicare Card',               back: false },
];
const DOC_TYPES_INTL = [
  { id: 'passport',    label: 'Passport',       back: false },
  { id: 'national_id', label: 'National ID',    back: true  },
  { id: 'student_id',  label: 'Student ID',     back: true  },
];

async function uploadToCloudinary(file, token) {
  const sig = await fetch(`${API}/api/cloudinary/signature?folder=rentalx/kyc`, {
    headers: { Authorization: `Bearer ${token}` },
  }).then(r => r.json());
  const form = new FormData();
  form.append('file', file);
  form.append('api_key',   sig.api_key);
  form.append('timestamp', sig.timestamp);
  form.append('signature', sig.signature);
  form.append('folder',    sig.folder);
  const res = await fetch(`https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`, { method: 'POST', body: form });
  const d = await res.json();
  return d.secure_url;
}

export default function VerificationFlow({ token, onClose, onComplete }) {
  const [step, setStep]           = useState(1);   // 1=region, 2=doctype, 3=upload, 4=done
  const [isAU, setIsAU]           = useState(true);
  const [docType, setDocType]     = useState(null);
  const [frontUrl, setFrontUrl]   = useState('');
  const [backUrl, setBackUrl]     = useState('');
  const [selfieUrl, setSelfieUrl] = useState('');
  const [uploading, setUploading] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]         = useState('');

  const docTypes = isAU ? DOC_TYPES_AU : DOC_TYPES_INTL;
  const selectedDoc = docTypes.find(d => d.id === docType);

  const handleFile = async (file, field) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setError('Max 5MB per file.'); return; }
    setUploading(field); setError('');
    try {
      const url = await uploadToCloudinary(file, token);
      if (field === 'front')  setFrontUrl(url);
      if (field === 'back')   setBackUrl(url);
      if (field === 'selfie') setSelfieUrl(url);
    } catch { setError('Upload failed. Please try again.'); }
    finally { setUploading(''); }
  };

  const handleSubmit = async () => {
    if (!frontUrl) { setError('Please upload the front of your document.'); return; }
    if (selectedDoc?.back && !backUrl) { setError('Please upload the back of your document.'); return; }
    setSubmitting(true); setError('');
    try {
      const res = await fetch(`${API}/api/verification/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ document_type: docType, front_url: frontUrl, back_url: backUrl, selfie_url: selfieUrl }),
      });
      if (res.ok) { setStep(4); onComplete?.(); }
      else setError('Submission failed. Please try again.');
    } catch { setError('Network error. Please try again.'); }
    finally { setSubmitting(false); }
  };

  const FileUploadZone = ({ field, label, url }) => (
    <label className={`relative flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-2xl p-6 cursor-pointer transition-all ${
      url ? 'border-[#2cbdc4] bg-[#2cbdc4]/5' : 'border-gray-200 hover:border-[#094f6f]/40 hover:bg-gray-50'
    }`}>
      <input type="file" accept="image/*,.pdf" className="hidden"
        onChange={e => e.target.files?.[0] && handleFile(e.target.files[0], field)} />
      {uploading === field ? (
        <Loader className="w-6 h-6 text-[#094f6f] animate-spin" />
      ) : url ? (
        <><Check className="w-6 h-6 text-[#2cbdc4]" /><p className="text-xs text-[#2cbdc4] font-semibold">Uploaded ✓</p></>
      ) : (
        <><Upload className="w-6 h-6 text-gray-400" /><p className="text-xs text-gray-500 font-medium">{label}</p><p className="text-xs text-gray-400">JPG, PNG, PDF · max 5MB</p></>
      )}
    </label>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(4,30,43,0.8)', backdropFilter: 'blur(8px)' }}>
      <div className="bg-white rounded-3xl w-full max-w-md shadow-2xl overflow-hidden">
        <div className="h-1.5 w-full" style={{ background: BRAND }} />
        <div className="p-7">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-black text-gray-900">Verify Your ID</h2>
            <button onClick={onClose} className="w-8 h-8 rounded-xl bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors">
              <X className="w-4 h-4 text-gray-500" />
            </button>
          </div>

          {step === 1 && (
            <div className="space-y-4">
              <p className="text-sm text-gray-500 mb-5">Are you an Australian resident or an international user?</p>
              {[
                { val: true,  label: 'Australian Resident', sub: 'Driver\'s Licence, Passport, or Medicare Card' },
                { val: false, label: 'International',       sub: 'Passport, National ID, or Student ID' },
              ].map(opt => (
                <button key={String(opt.val)} onClick={() => { setIsAU(opt.val); setStep(2); }}
                  className="w-full flex items-start gap-3 p-4 border-2 border-gray-200 rounded-2xl hover:border-[#2cbdc4] transition-all text-left">
                  <div>
                    <p className="font-bold text-gray-900 text-sm">{opt.label}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{opt.sub}</p>
                  </div>
                </button>
              ))}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-3">
              <p className="text-sm text-gray-500 mb-5">Select your document type</p>
              {docTypes.map(dt => (
                <button key={dt.id} onClick={() => { setDocType(dt.id); setStep(3); }}
                  className="w-full flex items-center justify-between p-4 border-2 border-gray-200 rounded-2xl hover:border-[#2cbdc4] transition-all text-left">
                  <p className="font-medium text-gray-800 text-sm">{dt.label}</p>
                  {dt.back && <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">Front + Back</span>}
                </button>
              ))}
              <button onClick={() => setStep(1)} className="text-xs text-gray-400 hover:text-gray-600 mt-2">← Back</button>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <p className="text-sm text-gray-500 mb-1">Upload your {selectedDoc?.label}</p>
              <FileUploadZone field="front" label="Front of document" url={frontUrl} />
              {selectedDoc?.back && <FileUploadZone field="back" label="Back of document" url={backUrl} />}

              <div className="border-t border-gray-100 pt-4">
                <p className="text-xs text-gray-400 mb-2 font-medium">Optional — Selfie verification</p>
                <label className={`flex items-center gap-3 p-3 border rounded-xl cursor-pointer transition-all ${
                  selfieUrl ? 'border-[#2cbdc4] bg-[#2cbdc4]/5' : 'border-gray-200 hover:border-[#094f6f]/30'
                }`}>
                  <input type="file" accept="image/*" capture="user" className="hidden"
                    onChange={e => e.target.files?.[0] && handleFile(e.target.files[0], 'selfie')} />
                  {uploading === 'selfie' ? <Loader className="w-4 h-4 animate-spin text-[#094f6f]" /> :
                    selfieUrl ? <Check className="w-4 h-4 text-[#2cbdc4]" /> : <Camera className="w-4 h-4 text-gray-400" />}
                  <span className="text-xs text-gray-500">{selfieUrl ? 'Selfie uploaded ✓' : 'Take a selfie to match with document'}</span>
                </label>
              </div>

              {error && <p className="text-red-500 text-xs">{error}</p>}

              <button onClick={handleSubmit} disabled={submitting || !frontUrl}
                className="w-full py-3.5 rounded-2xl font-bold text-white text-sm disabled:opacity-50 hover:opacity-90 transition-opacity"
                style={{ background: BRAND }}>
                {submitting ? <span className="flex items-center justify-center gap-2"><Loader className="w-4 h-4 animate-spin" /> Submitting…</span> : 'Submit for Verification'}
              </button>
              <button onClick={() => setStep(2)} className="text-xs text-gray-400 hover:text-gray-600 block text-center">← Back</button>
            </div>
          )}

          {step === 4 && (
            <div className="text-center py-4">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-5">
                <Check className="w-8 h-8 text-green-500" />
              </div>
              <h3 className="text-xl font-black text-gray-900 mb-2">Submitted!</h3>
              <p className="text-gray-500 text-sm mb-2">Your ID is under review — we&apos;ll verify within 24 hours.</p>
              <p className="text-xs text-gray-400 mb-6">You&apos;ll receive a notification when approved and 500 pts will be added to your account.</p>
              <button onClick={onClose}
                className="w-full py-3 rounded-2xl font-bold text-white text-sm hover:opacity-90 transition-opacity"
                style={{ background: BRAND }}>
                Back to Dashboard
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
